import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHero, SectionEyebrow } from "@/components/Primitives";
import SEO from "@/lib/seo";
import { Send, FileText } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Essay", "Editorial", "Research", "Analysis", "Healthcare", "Framework"];

export default function SubmitInsight() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "",
    author_name: user?.name || "",
    author_email: user?.email || "",
    organisation: "",
    category: "Essay",
    excerpt: "",
    content: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.content.length < 300) {
      toast.error("Submissions should be at least 300 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submissions", form);
      toast.success("Thank you. The Institute's editors will review within fourteen working days.");
      navigate("/insights");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="submit-insight-page">
      <SEO title="Submit your essay · The Cleanability Institute" description="Submit an essay or research note for editorial review." path="/insights/submit" />
      <PageHero
        testId="submit-hero"
        eyebrow="Editorial submissions"
        title="Submit your essay."
        lead="The Institute publishes work by practitioners, researchers, students and operators across the world. Editorial reviews complete within fourteen working days. Markdown is supported."
      />

      <section className="container-x pb-24 border-t border-border pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4 space-y-6">
            <SectionEyebrow>What we publish</SectionEyebrow>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>· Essays and editorial commentary (~1,200–3,500 words)</li>
              <li>· Research notes and short empirical studies</li>
              <li>· Sector case observations</li>
              <li>· Philosophical and ethical reflections on maintenance work</li>
            </ul>
            <p className="text-xs text-muted-foreground">By submitting you confirm the work is your own and grant the Institute permission to edit and publish under your byline.</p>
          </div>

          <form onSubmit={submit} data-testid="submit-form" className="lg:col-span-8 rounded-3xl border border-border bg-card p-8 md:p-10 space-y-5">
            <FormField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testId="submit-title" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label="Your name" value={form.author_name} onChange={(v) => setForm({ ...form, author_name: v })} required testId="submit-author-name" />
              <FormField label="Email" type="email" value={form.author_email} onChange={(v) => setForm({ ...form, author_email: v })} required testId="submit-author-email" />
              <FormField label="Organisation" value={form.organisation} onChange={(v) => setForm({ ...form, organisation: v })} testId="submit-organisation" />
              <label className="block">
                <div className="text-xs font-mono-mini text-muted-foreground">Category</div>
                <select
                  data-testid="submit-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <div>
              <label className="text-xs font-mono-mini text-muted-foreground">Standfirst / excerpt</label>
              <textarea data-testid="submit-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} required rows={3}
                className="mt-2 w-full px-3 py-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]" />
            </div>
            <div>
              <label className="text-xs font-mono-mini text-muted-foreground">Body (Markdown supported)</label>
              <textarea data-testid="submit-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={14}
                className="mt-2 w-full px-3 py-3 rounded-md border border-border bg-background text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]" />
              <p className="mt-1 text-[11px] text-muted-foreground">{form.content.length} characters · minimum 300</p>
            </div>
            <button type="submit" disabled={submitting} data-testid="submit-button"
              className="inline-flex items-center gap-2 h-12 px-5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-60">
              {submitting ? "Submitting…" : <>Submit for review <Send className="h-4 w-4" /></>}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function FormField({ label, type = "text", value, onChange, required, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-mini text-muted-foreground">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full h-11 px-3 rounded-md border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
      />
    </label>
  );
}
